import * as React from 'react';
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation
} from 'react-router-dom';

import { getSession } from './redux/modules/auth';

import App from './app';

import Frontpage from './layout/frontpage';
import NotFound from './layout/not-found';

import Signup from './profile/signup';
import ForgotPassword from './profile/forgot-password';
import ResetPassword from './profile/reset-password';
import Profile from './profile';
import Statistics from './statistics';
import Messages from './messages';
import Dashboard from './dashboard';

import DwellingUpdate from './dwellings/update';
import DwellingView from './dwellings/view';
import StatusView from './dwellings/statusView';

import Activity from './activity';
import Discounts from './discounts';
import Feedback from './feedback';
import Users from './users';
import Boligsoegerlisten from './boligsoegerlisten';
import Thread from './thread';
import Payments from './payments';
import StripeDetail from './payments/stripeDetail';

function AuthInit({ store }) {
  React.useEffect(() => {
    const auth = store.getState().auth;

    if (auth.token && !auth.user) {
      store.dispatch(getSession());
    }
  }, [store]);

  return <Outlet />;
}

function RequireAuth({ store }) {
  const location = useLocation();
  const [checking, setChecking] = React.useState(true);
  const [allowed, setAllowed] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      const auth = store.getState().auth;

      if (auth.user) {
        if (mounted) {
          setAllowed(true);
          setChecking(false);
        }
        return;
      }

      if (auth.token) {
        try {
          const action = await store.dispatch(getSession());
          const token =
            action &&
            action.payload &&
            action.payload.result &&
            action.payload.result.token;

          if (mounted) {
            setAllowed(Boolean(token));
            setChecking(false);
          }
          return;
        } catch (err) {
          if (__DEV__) {
            console.error(err);
          }
        }
      }

      if (mounted) {
        setAllowed(false);
        setChecking(false);
      }
    }

    checkAuth();

    return () => {
      mounted = false;
    };
  }, [store, location.pathname]);

  if (checking) {
    return null;
  }

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

export default function getRoutes(store) {
  return (
    <Routes>
      <Route element={<AuthInit store={store} />}>
        <Route path="/" element={<App />}>
          <Route index element={<Frontpage />} />

          <Route path="signup" element={<Signup />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="status" element={<StatusView />} />

          <Route element={<RequireAuth store={store} />}>
            <Route path="profile" element={<Profile />} />

            <Route path="dwellings">
              <Route index element={<Dashboard />} />
              <Route path="create" element={<DwellingUpdate />} />
              <Route path=":uuid" element={<DwellingUpdate />} />
              <Route path=":uuid/preview" element={<DwellingView />} />
              <Route path=":uuid/status" element={<StatusView />} />
            </Route>

            <Route path="activity" element={<Activity />} />
            <Route path="discounts" element={<Discounts />} />

            <Route path="payments">
              <Route index element={<Payments />} />
              <Route path="stripe/:paymentId" element={<StripeDetail />} />
            </Route>

            <Route path="feedback" element={<Feedback />} />

            <Route path="users">
              <Route index element={<Users />} />
              <Route path=":uuid" element={<Users />} />
            </Route>

            <Route path="statistics" element={<Statistics />} />

            <Route path="messages">
              <Route index element={<Messages />} />
              <Route path=":threadId" element={<Thread />} />
            </Route>

            <Route path="boligsoegerlisten" element={<Boligsoegerlisten />} />

            <Route path="dashboard" element={<Dashboard />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}